import { useEffect } from "react";
import { useStore } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import { DateRange } from "@corpuscomplete/core/common/date-time";
import { MealPlanForm } from "@corpuscomplete/core-ui/nutrition/meal-plan/form";
import { Button } from "@corpuscomplete/web-ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@corpuscomplete/web-ui/components/dialog";
import { Input } from "@corpuscomplete/web-ui/components/input";
import { Label } from "@corpuscomplete/web-ui/components/label";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { FormErrors } from "@corpuscomplete/web/hooks/create-form-hook";

function FieldErrors({ errors }: { errors: unknown }) {
  return <FormErrors errors={errors as never} />;
}

export function CreateMealPlanDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();

  return (
    <MealPlanForm.CreateProvider
      onSubmitting={() => {
        props.onOpenChange(false);
      }}
      onCreated={({ mealPlanId }) => {
        void navigate({
          to: "/personal/meal-plans/$id",
          params: { id: mealPlanId },
        });
      }}
    >
      <CreateMealPlanDialogContent open={props.open} onOpenChange={props.onOpenChange} />
    </MealPlanForm.CreateProvider>
  );
}

function CreateMealPlanDialogContent(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const createForm = MealPlanForm.useCreate();
  const isOverlapping = MealPlanForm.useIsOverlapping();
  const isSubmitting = useStore(createForm.store, (state) => state.isSubmitting);
  const values = useStore(createForm.store, (state) => state.values);
  const rangeSummary = getRangeSummary(values.dateRange);

  useEffect(() => {
    if (!props.open && !isSubmitting) {
      createForm.reset();
    }
  }, [createForm, isSubmitting, props.open]);

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Meal Plan</DialogTitle>
          <DialogDescription>Choose the dates for your new meal plan draft.</DialogDescription>
        </DialogHeader>

        <createForm.AppForm>
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <createForm.AppField name="dateRange.dateStart">
                {(field) => (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="meal-plan-date-start" className="text-xs leading-none">
                      Start Date
                    </Label>
                    <Input
                      id="meal-plan-date-start"
                      type="date"
                      value={field.state.value.toString()}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(Temporal.PlainDate.from(event.target.value))
                      }
                    />
                    <FieldErrors errors={field.state.meta.errors} />
                  </div>
                )}
              </createForm.AppField>

              <createForm.AppField name="dateRange.dateEnd">
                {(field) => (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="meal-plan-date-end" className="text-xs leading-none">
                      End Date
                    </Label>
                    <Input
                      id="meal-plan-date-end"
                      type="date"
                      value={field.state.value.toString()}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(Temporal.PlainDate.from(event.target.value))
                      }
                    />
                    <FieldErrors errors={field.state.meta.errors} />
                  </div>
                )}
              </createForm.AppField>
            </div>

            {rangeSummary.durationLabel ? (
              <Text className="text-sm text-muted-foreground">{rangeSummary.durationLabel}</Text>
            ) : null}

            {!isSubmitting && !rangeSummary.hasInvalidOrder && isOverlapping ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Dates overlap with an existing meal plan.
              </div>
            ) : null}

            <createForm.Subscribe selector={(state) => state.errors}>
              {(errors) => <FieldErrors errors={errors} />}
            </createForm.Subscribe>

            <DialogFooter className="gap-2 sm:justify-end">
              <Button variant="outline" onClick={() => props.onOpenChange(false)}>
                Cancel
              </Button>
              <Button disabled={isSubmitting} onClick={() => void createForm.handleSubmit()}>
                {isSubmitting ? "Creating..." : "Create Meal Plan"}
              </Button>
            </DialogFooter>
          </div>
        </createForm.AppForm>
      </DialogContent>
    </Dialog>
  );
}

function getRangeSummary(dateRange: DateRange.Closed) {
  try {
    const { durationDays } = DateRange.getSummary(dateRange);

    return {
      label: DateRange.format(dateRange, {
        dateFormat: "longMonthDayYear",
        separator: " - ",
      }),
      hasInvalidOrder: durationDays <= 0,
      durationLabel: durationDays > 0 ? DateRange.formatDuration(dateRange) : null,
    };
  } catch {
    return {
      label: "Choose a start and end date for this meal plan.",
      hasInvalidOrder: false,
      durationLabel: null,
    };
  }
}
